/* eslint-disable max-lines */
import React from 'react'
import { tagAxes, getDetailedTags, getFamilyTree } from '@cpsy-rmap-project/tags'
import { getSearchTypeLabel } from '@cpsy-rmap-project/web-core/crossref'
import { DetailedPaper } from '@cpsy-rmap-project/web-core/repositories'
import { isAdmin } from '@cpsy-rmap-project/web-core/utils'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import MuiDialogActions from '@material-ui/core/DialogActions'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import _, { isEmpty, isNil } from 'lodash'
import { useLocation } from 'react-router'
import { Routes } from '../../../consts'
import { formattedDate } from '../../../utils/date'
import Pill from '../../atoms/Pill'
import WithNewLine from '../../atoms/WithNewLine'
import { useEditModelDialog } from '../../molecules/EditModelDialog/hooks'
import { useLogin } from '../../organisms/Login'
import { usePaper } from '../../organisms/Paper'
import DeleteDialog from './DeleteDialog'
import { usePaperDetailsModal } from './hooks'
import { InsideButton } from './InsideButton'
import { OutsideButton } from './OutsideButton'
import { OutsideRequestButton } from './OutsideRequestButton'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2)
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    }
  })

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string
  children: React.ReactNode
  onClose: () => void
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  )
})

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2)
  }
}))(MuiDialogContent)

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1)
  }
}))(MuiDialogActions)

const ModifyButtonBox: React.FC<{
  text: string
  color: 'primary' | 'secondary'
  onClick: () => void
}> = ({ text, color, onClick }) => (
  <Box style={{ textAlign: 'center', margin: '10px' }}>
    <Button variant="outlined" color={color} size="medium" onClick={onClick}>
      {text}
    </Button>
  </Box>
)

const viewText = (str: string) => (!isEmpty(str) ? str : ' ')

// eslint-disable-next-line max-lines-per-function
export default function CustomizedDialogs() {
  const { open, paperId, handlePaperDetailsModalClose } = usePaperDetailsModal()
  const { papers: insidePapers, outsidePapers } = usePaper()
  const papers = React.useMemo(() => [...insidePapers, ...outsidePapers], [insidePapers, outsidePapers])
  const { fbUser } = useLogin()
  const location = useLocation()
  const isAdminAction = location.pathname.includes(Routes.Admin.path) && isAdmin({ fbUser })

  const paper = React.useMemo(() => papers.find(p => p.id === paperId), [paperId, papers])

  const editModelDialog = useEditModelDialog()

  const isOwner = !_.isNil(fbUser) && !_.isNil(paper) && (fbUser.uid === paper.uid || isAdmin({ fbUser }))

  if (!paper) {
    return <></>
  }

  return (
    <>
      <Dialog
        onClose={handlePaperDetailsModalClose}
        aria-labelledby="customized-dialog-title"
        maxWidth="lg"
        fullWidth
        open={open}
      >
        <DialogTitle id="customized-dialog-title" onClose={handlePaperDetailsModalClose}>
          Paper Details
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h2" gutterBottom>
            Basic info.
          </Typography>
          <Box style={{ paddingLeft: '1rem' }}>
            <>
              <Typography variant="h4">Title</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {viewText(paper.title)}
              </Typography>
            </>

            <>
              <Typography variant="h4">URL</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                <a href={paper.url} target="_blank" rel="noopener noreferrer">
                  {viewText(paper.url)}
                </a>
              </Typography>
            </>

            <>
              <Typography variant="h4">Authors</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {viewText(paper.authors)}
              </Typography>
            </>

            <>
              <Typography variant="h4">Year</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {viewText(String(paper.year))}
              </Typography>
            </>

            <>
              <Typography variant="h4">Journal</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {viewText(paper.journal)}
              </Typography>
            </>

            <>
              <Typography variant="h4">Abstract</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                <WithNewLine str={viewText(paper.abstract)} />
              </Typography>
            </>
          </Box>

          <Divider style={{ margin: '10px 0' }} />
          <Typography variant="h2" gutterBottom>
            Tag info.
          </Typography>
          <Box style={{ paddingLeft: '1rem' }}>
            {tagAxes.map(tagAxis => (
              <Box key={tagAxis.id} style={{ marginBottom: '0.5rem' }}>
                <Typography variant="h4" style={{ display: 'inline-block' }}>
                  {`${tagAxis.name}: `}
                </Typography>
                {getDetailedTags(paper[tagAxis.id]).map((detailedTag, detailedTagIndex) => {
                  const familyTree = getFamilyTree(detailedTag)
                  return (
                    <span key={`${tagAxis.id}_${detailedTag.id}`} style={{ margin: '0 4px', display: 'inline-block' }}>
                      {familyTree.map((tag, index) => (
                        <React.Fragment key={tag.id}>
                          <Pill id={tag.id} clickable />
                          {index !== familyTree.length - 1 && ' -> '}
                        </React.Fragment>
                      ))}
                      {detailedTagIndex !== getDetailedTags(paper[tagAxis.id]).length - 1 && ', '}
                    </span>
                  )
                })}
              </Box>
            ))}
          </Box>

          <Divider style={{ margin: '10px 0' }} />
          <Typography variant="h2" gutterBottom>
            Other info.
          </Typography>
          <Box style={{ paddingLeft: '1rem' }}>
            <>
              <Typography variant="h4">Hypothesis</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                <WithNewLine str={viewText(paper.hypothesis)} />
              </Typography>
            </>

            <>
              <Typography variant="h4">Implication</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                <WithNewLine str={viewText(paper.prediction)} />
              </Typography>
            </>

            <>
              <Typography variant="h4">Related papers</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                <WithNewLine str={viewText(paper.relevantPapers)} />
              </Typography>
            </>

            <>
              <Typography variant="h4">Comments</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                <WithNewLine str={viewText(paper.comment)} />
              </Typography>
            </>
          </Box>

          <Divider style={{ margin: '10px 0' }} />
          <Typography variant="h2" gutterBottom>
            System info.
          </Typography>
          <Box style={{ paddingLeft: '1rem' }}>
            <>
              <Typography variant="h4">DOI</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {viewText(paper.doi)}
              </Typography>
            </>
            <>
              <Typography variant="h4">Type</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {viewText(getSearchTypeLabel(paper.type))}
              </Typography>
            </>
            <>
              <Typography variant="h4">Cited count</Typography>
              {!isNil(paper.citedCount) ? (
                <>
                  <Typography variant="body2" color="textPrimary">
                    {paper.citedCount}
                  </Typography>
                  {paper.crid ? (
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      {` (Last automatically updated at: ${formattedDate(paper.crossrefUpdatedAt)} by CrossRef)`}
                    </Typography>
                  ) : (
                    paper.pmid && (
                      <Typography variant="caption" color="textSecondary" gutterBottom>
                        {` (Last automatically updated at: ${formattedDate(paper.pubmedUpdatedAt)} by Pubmed)`}
                      </Typography>
                    )
                  )}
                </>
              ) : (
                <Typography variant="caption" color="textSecondary" component="div" gutterBottom>
                  {!paper.crid && !paper.pmid
                    ? '* If you register manually, "Cited Count" will not be updated. If necessary, please re-register with search for paper data.'
                    : '* At the moment, no "Cited Count" data in CrossRef/Pubmed'}
                </Typography>
              )}
            </>

            <>
              <Typography variant="h4">Registration process</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {paper.draftByAutoTag ? 'Automatic Registration' : 'Human Registration'}
              </Typography>
            </>

            <>
              <Typography variant="h4">Created at</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {formattedDate(paper.createdAt)}
              </Typography>
            </>

            <>
              <Typography variant="h4">Updated at</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {formattedDate(paper.updatedAt)}
              </Typography>
            </>

            {paper.pmid && (
              <>
                <Typography variant="h4">Pubmed ID</Typography>
                <Typography variant="body2" color="textPrimary" gutterBottom>
                  {paper.pmid}
                </Typography>
              </>
            )}

            <>
              <Typography variant="h4">System ID</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {paper.id}
              </Typography>
            </>

            <>
              <Typography variant="h4">Status</Typography>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {getPaperStatus(paper)}
              </Typography>
            </>
          </Box>

          <Divider style={{ margin: '10px 0' }} />

          {isAdminAction ? (
            <>
              <InsideButton paper={paper} />
              <OutsideButton paper={paper} />
            </>
          ) : (
            <>
              <ModifyButtonBox
                text={isOwner ? 'Edit' : 'Edit Request'}
                color="primary"
                onClick={() => {
                  editModelDialog.handle.handleOpen(paper, isOwner, true)
                }}
              />

              <OutsideRequestButton paper={paper} />
            </>
          )}

          {isOwner && (
            <Box style={{ marginTop: 60 }}>
              <DeleteDialog paper={paper} />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <Button autoFocus onClick={handlePaperDetailsModalClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const getPaperStatus = (paper: Pick<DetailedPaper, 'outside' | 'outsideRequest'>) => {
  if (paper.outsideRequest && !paper.outside) {
    return 'Outside Requested'
  }
  if (paper.outside) {
    return 'Outsided'
  }
  return 'Registerd'
}
